import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/** A modal sheet on the native <dialog>, so focus, Escape and the backdrop behave as expected. */
export function Sheet({ open, onClose, title, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-labelledby="sheet-title"
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Only a tap outside the sheet closes it (the dialog element includes its padding).
        const box = ref.current?.getBoundingClientRect();
        if (e.target !== ref.current || !box) return;
        const inside = e.clientX >= box.left && e.clientX <= box.right && e.clientY >= box.top && e.clientY <= box.bottom;
        if (!inside) onClose();
      }}
    >
      {open && (
        <div className="stack-lg">
          <h2 id="sheet-title" className="section-title" style={{ fontSize: '1.25rem' }}>
            {title}
          </h2>
          {children}
        </div>
      )}
    </dialog>
  );
}
